/**
 * Analytics consent for the dashboard, the same rule as the website's
 * analytics-consent.js (apps/website/src/analytics-consent.js).
 *
 * Funnel events go to api.ada-tool.com and keep a user_id / session_id in
 * localStorage. Storing an identifier for analytics is not strictly
 * necessary (ePrivacy Art 5(3)), so nothing is sent or stored until the
 * user accepts, and withdrawing takes the same one click as giving
 * (GDPR Art 7(3)): the footer's "Analytics settings" reopens the choice.
 *
 * The dashboard is its own origin, so the website's choice does not carry
 * here; the key name is the same so both read alike.
 */

export const ANALYTICS_CONSENT_KEY = 'ce-analytics-consent';
export const ANALYTICS_SETTINGS_EVENT = 'ce-analytics-settings';

export type AnalyticsConsent = 'granted' | 'denied';

export function readAnalyticsConsent(): AnalyticsConsent | null {
  try {
    const v = localStorage.getItem(ANALYTICS_CONSENT_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null; // storage blocked: undecided, never assume consent
  }
}

/** The two identifiers the analytics service keeps in this browser. */
export function clearAnalyticsIds(): void {
  try {
    localStorage.removeItem('user_id');
    localStorage.removeItem('session_id');
  } catch {
    /* nothing stored */
  }
}

export function setAnalyticsConsent(value: AnalyticsConsent): void {
  try {
    localStorage.setItem(ANALYTICS_CONSENT_KEY, value);
  } catch {
    /* storage blocked: the choice is not remembered */
  }
  if (value === 'denied') clearAnalyticsIds();
}

/** Footer "Analytics settings": reopen the consent card. */
export function openAnalyticsSettings(): void {
  window.dispatchEvent(new Event(ANALYTICS_SETTINGS_EVENT));
}
